import { formatDate } from './dateFormat'
import { isRecord, responseErrorState } from './entityResponse'

export const TRANSFER_DOCUMENT_TYPES = Object.freeze([
  { value: 'upd', label: 'УПД' },
  { value: 'avance', label: 'Счет-фактура на аванс' },
  { value: 'payment', label: 'Платежное поручение' }
])

export const TRANSFER_DIRECTIONS = Object.freeze([
  { value: 'in', label: 'Входящий документ' },
  { value: 'out', label: 'Исходящий документ' }
])

const COMMON_UPD_FIELDS = Object.freeze([
  { value: 'count', label: 'Количество' },
  { value: 'price', label: 'Цена' },
  { value: 'cost_without_nds', label: 'Стоимость до налогов' },
  { value: 'aciz', label: 'Акциз' },
  { value: 'nalog', label: 'Сумма налога, всего' },
  { value: 'summtotal', label: 'Сумма с налогом, всего' },
  { value: 'sebes_average', label: 'Себестоимость (средняя)' }
])

const AMOUNT_FIELDS = Object.freeze({
  upd: COMMON_UPD_FIELDS,
  avance: Object.freeze([
    { value: 'summtotal', label: 'Сумма всего' },
    { value: 'nalog', label: 'Сумма налога' }
  ]),
  payment: Object.freeze([
    { value: 'summ', label: 'Сумма' },
    { value: 'nds_summ', label: 'Сумма НДС' }
  ])
})

const DOCUMENT_ROUTES = Object.freeze({
  upd: 'document_info_upd',
  sale: 'document_info_upd',
  purchase: 'document_info_upd',
  payment: 'document_info_payment',
  avance: 'document_info_factura_avance',
  factura_avance: 'document_info_factura_avance',
  accept_factura_avance: 'document_info_factura_avance',
  bill_to_pay: 'document_info_bill_to_pay',
  act_sver: 'document_info_act_sver'
})

let localLineId = 0

function stableLineKey(line, index) {
  if (line.id !== undefined && line.id !== null) return `line-${line.id}`
  localLineId += 1
  return `new-line-${index}-${localLineId}`
}

export function amountFieldOptions(documentType) {
  return AMOUNT_FIELDS[documentType] || COMMON_UPD_FIELDS
}

export function createTransferTemplateLine(source = {}, index = 0) {
  return {
    ...source,
    _key: source._key || stableLineKey(source, index),
    line_order: index + 1,
    debit_account_id: source.debit_account_id ?? null,
    credit_account_id: source.credit_account_id ?? null,
    amount_source: source.amount_source || null,
    description_template: String(source.description_template || '')
  }
}

export function createTransferTemplateModel(source = {}) {
  const lines = Array.isArray(source.lines) ? source.lines : []
  return {
    code: source.code || null,
    name: String(source.name || ''),
    description: String(source.description || ''),
    document_type: source.document_type || 'upd',
    operation_type: source.operation_type || 'document',
    direction: source.direction || 'in',
    metadata: isRecord(source.metadata) ? { ...source.metadata } : {},
    lines: lines.map((line, index) => createTransferTemplateLine(line, index))
  }
}

export function validateStoredTemplate(template) {
  if (!isRecord(template)) {
    return { ok: false, state: 'error', message: 'Сервер вернул некорректные данные шаблона проводки' }
  }
  if (template.is_active === false || template.archived_at) {
    return { ok: false, state: 'not-found', message: 'Шаблон архивирован и недоступен для редактирования.' }
  }
  if (!Array.isArray(template.lines) || template.lines.length === 0) {
    return { ok: false, state: 'error', message: 'В шаблоне отсутствуют строки проводок. Исправьте данные шаблона на сервере.' }
  }
  const malformedIndex = template.lines.findIndex((line) => (
    !isRecord(line) ||
    line.debit_account_id === undefined || line.debit_account_id === null ||
    line.credit_account_id === undefined || line.credit_account_id === null ||
    !line.amount_source
  ))
  if (malformedIndex >= 0) {
    return {
      ok: false,
      state: 'error',
      message: `Строка ${malformedIndex + 1} шаблона повреждена: не указан счет или источник суммы.`
    }
  }
  return { ok: true, model: createTransferTemplateModel(template) }
}

export function normalizeTransferTemplateList(response) {
  if (!isRecord(response) || response.type === 'error' || response.error) {
    return { ok: false, ...responseErrorState(response, 'Не удалось загрузить шаблоны проводок') }
  }
  if (!Array.isArray(response.templates)) {
    return { ok: false, state: 'error', message: 'Сервер вернул некорректный список шаблонов проводок' }
  }
  return {
    ok: true,
    rows: response.templates.filter(isRecord).map((template, index) => ({
      ...template,
      id: template.id ?? `template-${index}`,
      name: String(template.name || 'Шаблон без названия'),
      lines: Array.isArray(template.lines) ? template.lines.filter(isRecord) : [],
      is_active: template.is_active !== false && !template.archived_at
    }))
  }
}

export function normalizeTransfers(response) {
  if (!isRecord(response) || response.type === 'error' || response.error) {
    return { ok: false, ...responseErrorState(response, 'Не удалось загрузить проводки') }
  }
  if (!Array.isArray(response.transfers)) {
    return { ok: false, state: 'error', message: 'Сервер вернул некорректный список проводок' }
  }
  return {
    ok: true,
    rows: response.transfers.filter(isRecord).map((transfer, index) => ({
      ...transfer,
      id: transfer.id ?? `transfer-${index}`,
      date_display: formatDate(transfer.date, transfer.date || '—'),
      debit_account: transfer.debit_account || transfer.bill_to || '—',
      credit_account: transfer.credit_account || transfer.bill_from || '—',
      amount: transfer.amount ?? transfer.summ ?? null,
      document: String(transfer.document || 'Без документа'),
      user_display: String(transfer.user_display || transfer.user_name || transfer.user || '—')
    })),
    pagination: isRecord(response.pagination) ? response.pagination : {}
  }
}

export function transferDocumentRoute(transfer) {
  if (!transfer || transfer.document_id === undefined || transfer.document_id === null) return null
  const name = DOCUMENT_ROUTES[transfer.document_type]
  if (!name) return null
  return {
    name,
    params: { id: transfer.document_id, history_id: 'last' }
  }
}

export function transferTemplatePayload(model) {
  return {
    ...(model.code ? { code: model.code } : {}),
    name: String(model.name || '').trim(),
    description: String(model.description || ''),
    document_type: model.document_type,
    operation_type: model.operation_type || 'document',
    direction: model.direction,
    ...(isRecord(model.metadata) && Object.keys(model.metadata).length ? { metadata: model.metadata } : {}),
    lines: model.lines.map((line, index) => ({
      line_order: index + 1,
      amount_source: line.amount_source,
      credit_account_id: line.credit_account_id,
      debit_account_id: line.debit_account_id,
      description_template: String(line.description_template || ''),
      ...(line.amount_formula ? { amount_formula: line.amount_formula } : {}),
      ...(line.vat_rate_source ? { vat_rate_source: line.vat_rate_source } : {}),
      ...(isRecord(line.conditions) ? { conditions: line.conditions } : {}),
      ...(isRecord(line.analytics) ? { analytics: line.analytics } : {}),
      ...(isRecord(line.metadata) ? { metadata: line.metadata } : {}),
      ...(line.is_active === false ? { is_active: false } : {})
    }))
  }
}
